import {
  FormField,
  FormGrid,
  Page,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import audioManager, { NotificationSoundTypeT } from 'app/utils/audioManager';
import { HookFormSelectionList } from 'components/hookForm';
import {
  NOTIFICATION_SOUND_OPTIONS,
  OFFER_NOTIFICATION_SOUND_OPTIONS,
} from 'components/notifications/sagas/audio';
import { useBreakpointValue } from 'utils/mediaQuery';

import { ProfileFormFieldValues } from '../../profileForm.types';

export const ProfileFormSounds = (): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const { control, getValues } = useFormContext<ProfileFormFieldValues>();

  const handlePlaySound = useCallback(
    (name: string) => {
      const values = getValues();
      audioManager.stop();
      audioManager.play(values[name], name as NotificationSoundTypeT, false);
    },
    [getValues],
  );

  const offerSoundsFormFields = useMemo(
    (): JSX.Element[] =>
      Object.entries(offerSoundsAssets).map(([name, label]) => (
        <FormField key={name} span={isSmallScreen ? 6 : 3}>
          <HookFormSelectionList
            control={control}
            name={name as keyof ProfileFormFieldValues}
            label={label}
            rules={{
              required: 'Please select a sound',
            }}
            componentProps={{
              placeholder: 'Select a sound',
              suffix: {
                type: 'button',
                button: {
                  icon: 'volume-up',
                  onClick: (e) => {
                    e.stopPropagation();
                    handlePlaySound(name);
                  },
                  'aria-label': `Play ${label}`,
                },
              },
              getItemTitleByValue: (value) =>
                OFFER_NOTIFICATION_SOUND_OPTIONS.find(
                  (item) => item.value === value,
                )?.label ?? value,
            }}
          >
            {OFFER_NOTIFICATION_SOUND_OPTIONS.map(({ label, value }) => (
              <SelectionList.Item key={value} value={value}>
                <Text.Paragraph>{label}</Text.Paragraph>
              </SelectionList.Item>
            ))}
          </HookFormSelectionList>
        </FormField>
      )),
    [control, handlePlaySound, isSmallScreen],
  );

  return (
    <Page.Section title="Sound notifications">
      <FormGrid>
        {offerSoundsFormFields}

        <FormField span={isSmallScreen ? 6 : 3}>
          <HookFormSelectionList
            control={control}
            name="notification_sound"
            label="Notification sound"
            rules={{
              required: 'Please select a sound',
            }}
            componentProps={{
              placeholder: 'Select a sound',
              suffix: {
                type: 'button',
                button: {
                  icon: 'volume-up',
                  onClick: (e) => {
                    e.stopPropagation();
                    handlePlaySound('notification_sound');
                  },
                  'aria-label': `Play notification sound`,
                },
              },
              getItemTitleByValue: (value) =>
                NOTIFICATION_SOUND_OPTIONS.find((item) => item.value === value)
                  ?.label ?? value,
            }}
          >
            {NOTIFICATION_SOUND_OPTIONS.map(({ label, value }) => (
              <SelectionList.Item key={value} value={value}>
                <Text.Paragraph>{label}</Text.Paragraph>
              </SelectionList.Item>
            ))}
          </HookFormSelectionList>
        </FormField>
      </FormGrid>
    </Page.Section>
  );
};

const offerSoundsAssets: Record<string, string> = {
  offer_notification_phone: 'Phone sound',
  offer_notification_chat: 'Chat sound',
  offer_notification_email: 'Email sound',
  offer_notification_messaging: 'Messaging sound',
};
